import { MantineProvider } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { BrowserRouter } from "react-router";
import { adminBasePath } from "@/app/base-path";
import { DocumentTitleSync } from "@/app/document-title-sync";
import { themeFor } from "@/app/theme";
import { ColorThemeProvider, useColorTheme } from "@/app/use-color-theme";
import { MotionProvider } from "@/app/use-motion";
import { ContentSettingsProvider } from "@/features/content/content-settings-provider";

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "dayjs/locale/uk";

type AppProvidersProps = {
	children: React.ReactNode;
};

function ThemedApp({ children }: AppProvidersProps) {
	const { theme, scheme } = useColorTheme();

	return (
		<MantineProvider theme={themeFor(theme, scheme)} forceColorScheme={scheme}>
			<DatesProvider settings={{ locale: "uk", firstDayOfWeek: 1 }}>
				<Notifications position="top-right" />
				<BrowserRouter basename={adminBasePath() || "/"}>
					<DocumentTitleSync />
					{children}
				</BrowserRouter>
			</DatesProvider>
		</MantineProvider>
	);
}

export function AppProviders({ children }: AppProvidersProps) {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						retry: 1,
						refetchOnWindowFocus: false,
					},
				},
			}),
	);

	return (
		<QueryClientProvider client={queryClient}>
			<ColorThemeProvider>
				<MotionProvider>
					<ContentSettingsProvider>
						<ThemedApp>{children}</ThemedApp>
					</ContentSettingsProvider>
				</MotionProvider>
			</ColorThemeProvider>
		</QueryClientProvider>
	);
}
